import React, { useState } from 'react';
import {
    TextField,
    Checkbox,
    Button,
    FormControlLabel,
    Link,
    Box,
    InputLabel,
    Dialog,
    DialogContent,
    DialogTitle,
    InputAdornment,
    IconButton,
    Alert,
    useTheme,
    Typography,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import {  useNavigate } from 'react-router-dom';
import { signUp } from '../slices/loginSlice';
import PublicLayout from '../Layout/PublicLayout';
import PremiumCard from '../components/common/PremiumCard';

const SignUpForm = props => {
    const { onLogin } = props;
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [errorMsg, setErrorMsg] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [msgOpen, setMsgOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const theme = useTheme();

    const handleMsgClose = () => {
        setMsgOpen(false);
    };

    const handleSignUp = async event => {
        event.preventDefault();
        setLoading(true);

        if (!email) {
            setErrorMsg('Please enter email');
            setLoading(false);
        } else if (!password) {
            setErrorMsg('Please enter password');
             setLoading(false);
        } else if (!confirmPassword) {
            setErrorMsg('Please enter Confirm password');
             setLoading(false);
        } else {
            let loginData = {
                email: email,
                password: password,
                confirmPassword: confirmPassword
            };
            dispatch(signUp(loginData))
                .then(response => {
                    if (response?.meta?.requestStatus === 'fulfilled') {
                        setMsgOpen(true);
                        setSuccessMessage(response?.payload);
                        setTimeout(() => {
                            handleMsgClose();
                            navigate('/');
                        }, 2000);
                        setEmail('');
                        setPassword('');
                        setConfirmPassword('');
                    } else {
                        setErrorMsg(response?.error?.message);
                    }
                })
                .catch(error => {
                    setErrorMsg(error?.response?.message);
                })
                .finally(() => {
                     setLoading(false);
                });
        }
    };
    const handleErrorClear = () => {
        setErrorMsg(prevValue => (prevValue = ''));
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(prevShowPassword => !prevShowPassword);
    };
    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(prevShowPassword => !prevShowPassword);
    };

    return (
        <PublicLayout>
             <PremiumCard
                active={true}
                title="Admin Access"
                subtitle="Create Account"
            >
                <form onSubmit={handleSignUp} noValidate>
                     <Box sx={{ mb: 3, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Join Trendify Ads to manage your campaigns.
                        </Typography>
                    </Box>

                     {/* Email Input */}
                    <Box sx={{ mb: 2 }}>
                        <InputLabel sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 500 }}>Email Address</InputLabel>
                        <TextField
                            fullWidth
                            placeholder="name@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
                                    borderRadius: '12px',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: 'text.secondary', opacity: 0.7 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                     {/* Password Input */}
                    <Box sx={{ mb: 2 }}>
                        <InputLabel sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 500 }}>Password</InputLabel>
                        <TextField
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create a password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
                                    borderRadius: '12px',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: 'text.secondary', opacity: 0.7 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleTogglePasswordVisibility}
                                            edge="end"
                                        >
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                     {/* Confirm Password Input */}
                    <Box sx={{ mb: 2 }}>
                        <InputLabel sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 500 }}>Confirm Password</InputLabel>
                        <TextField
                            fullWidth
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm your password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
                                    borderRadius: '12px',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: 'text.secondary', opacity: 0.7 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleToggleConfirmPasswordVisibility}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                         <FormControlLabel
                            control={
                                <Checkbox
                                    checked={rememberMe}
                                    onChange={e => setRememberMe(e.target.checked)}
                                    color="primary"
                                    sx={{ '& .MuiSvgIcon-root': { borderRadius: '6px' } }}
                                />
                            }
                            label={<Typography variant="body2">Remember me</Typography>}
                        />
                    </Box>

                    {errorMsg && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                            {errorMsg}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            borderRadius: '12px',
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            boxShadow: `0 8px 16px -4px ${theme.palette.primary.main}66`,
                            '&:hover': {
                                boxShadow: `0 12px 20px -4px ${theme.palette.primary.main}88`,
                            }
                        }}
                    >
                         {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
                    </Button>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Already have an account?{' '}
                            <Link href="/" sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main' }}>
                                Log in
                            </Link>
                        </Typography>
                    </Box>

                </form>
            </PremiumCard>

             <Dialog open={msgOpen} onClose={handleMsgClose} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ textAlign: 'center', pt: 3}}>Success !</DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', minWidth: '250px' }}>
                        <Typography>{successMessage}</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </PublicLayout>
    );
};

export default SignUpForm;
